/**
 * SegmentTimeline - Container for timeline, ruler, playhead, and track
 */
import { useRef, useCallback, useState, useEffect } from 'react';
import { SegmentTrack } from './SegmentTrack';
import { TimeRuler } from '@/pages/ProjectEditor/components/TimeRuler';
import { Playhead } from '@/pages/ProjectEditor/components/Playhead';
import { getEffectiveDuration } from '../utils/segmentUtils';

const LABEL_WIDTH = 60;

export function SegmentTimeline({
  segments,
  totalDuration,
  zoomLevel,
  playheadPosition,
  selectedSegmentId,
  onSelectSegment,
  onUpdateSegment,
  onReorderSegments,
  onSeek,
  getAssetUrl,
  playheadRef
}) {
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Calculate pixels per millisecond
  const pxPerMs = (zoomLevel * 100) / 10000;
  const totalWidth = Math.max(totalDuration * pxPerMs, 500);

  // Handle scroll
  const handleScroll = useCallback((e) => {
    setScrollLeft(e.target.scrollLeft);
  }, []);

  // Handle click on timeline background to seek
  const handleTimelineClick = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollLeft - LABEL_WIDTH;
    const timeMs = Math.max(0, x / pxPerMs);
    onSeek(Math.min(timeMs, totalDuration));
  }, [scrollLeft, pxPerMs, totalDuration, onSeek]);

  // Keep playhead in view during playback
  useEffect(() => {
    if (!scrollRef.current) return;
    
    const playheadX = playheadPosition * pxPerMs + LABEL_WIDTH;
    const container = scrollRef.current;
    const viewStart = container.scrollLeft;
    const viewEnd = viewStart + container.clientWidth;
    
    // Auto-scroll when playhead approaches edge
    if (playheadX > viewEnd - 100) {
      container.scrollLeft = playheadX - container.clientWidth + 200;
    } else if (playheadX < viewStart + LABEL_WIDTH + 50) {
      container.scrollLeft = Math.max(0, playheadX - LABEL_WIDTH - 100);
    }
  }, [playheadPosition, pxPerMs]);

  return (
    <div 
      ref={containerRef}
      className="relative flex flex-col bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden"
    >
      {/* Time Ruler */}
      <TimeRuler
        totalDuration={totalDuration}
        pxPerMs={pxPerMs}
        totalWidth={totalWidth}
        labelWidth={LABEL_WIDTH}
        playheadPosition={playheadPosition}
        scrollLeft={scrollLeft}
        onSeek={onSeek}
      />
      
      {/* Scrollable track area */}
      <div
        ref={scrollRef}
        className="relative overflow-x-auto overflow-y-hidden"
        style={{ maxHeight: '150px' }}
        onScroll={handleScroll}
        onClick={handleTimelineClick}
      >
        <div style={{ width: totalWidth + LABEL_WIDTH, minWidth: '100%' }}>
          <SegmentTrack
            segments={segments}
            pxPerMs={pxPerMs}
            labelWidth={LABEL_WIDTH}
            scrollLeft={scrollLeft}
            selectedSegmentId={selectedSegmentId}
            onSelectSegment={onSelectSegment}
            onUpdateSegment={onUpdateSegment}
            onReorderSegments={onReorderSegments}
            getAssetUrl={getAssetUrl}
          />
        </div>
        
        {/* Playhead */}
        <Playhead
          ref={playheadRef}
          position={playheadPosition}
          pxPerMs={pxPerMs}
          labelWidth={LABEL_WIDTH}
          scrollLeft={scrollLeft}
          totalDuration={totalDuration}
          onSeek={onSeek}
          containerRef={scrollRef}
        />
      </div>
    </div>
  );
}

export default SegmentTimeline;
